import React, { useMemo, useState } from "react";
import { CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import moment from "moment";
import * as ROUTES from "@/constants/route";
import ProfilePanel from "./ProfilePanel";
import StatusBanner from "./StatusBanner";
import SectionNav, { Section } from "./SectionNav";
import AccountSections from "./AccountSections";
import ChangePasswordCard from "./ChangePasswordCard";
import MyApplicationCard from "./MyApplicationCard";
import { T } from "./tokens";
import { SectionData } from "./types";
import {
  computeCompleteness,
  getAccountStatus,
  getInitials,
  normalizeTier,
  tierLabel,
} from "./maps";

const useStyles = makeStyles((theme) => ({
  page: {
    background: T.contentBg,
    margin: theme.spacing(-3),
    padding: theme.spacing(3),
    minHeight: "calc(100vh - 64px)",
  },
  layout: {
    display: "flex",
    gap: 24,
    alignItems: "flex-start",
    animation: "$rvUp .3s ease both",
  },
  right: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 20 },
  "@keyframes rvUp": {
    from: { transform: "translateY(7px)" },
    to: { transform: "translateY(0)" },
  },
}));

interface Props {
  firebase: any;
  userDoc: any;
  kyc: any;
  accountDetails: any;
}

function buildSectionData(src: { user: any; kyc: any; accountDetails: any; email: string }): SectionData {
  const u = src.user || {};
  const k = src.kyc || {};
  const ids = k.ids || {};
  const name = k.name || {};
  const work = k.work || {};
  const cur = k.currentAddress || {};
  const ad = src.accountDetails || {};
  const fullName = [name.firstName || u.firstName, name.middleName, name.lastName || u.lastName, name.suffix]
    .filter((s: string) => s && s !== "")
    .join(" ");
  return {
    identity: {
      fullName,
      dob: k.dateOfBirth ? moment(k.dateOfBirth).format("LL") : "",
      nationality: k.nationality || "",
      tin: ids.TIN || "",
    },
    contact: {
      email: src.email || k.email || "",
      mobileNumber: u.mobileNumber || k.mobileNumber || "",
    },
    address: {
      street: cur.street || "",
      city: cur.city || "",
      province: cur.province || "",
      zip: cur.zip || "",
      country: cur.country || "",
    },
    employment: {
      primaryOccupation: work.primaryOccupation || "",
      nameOfEmployer: work.nameOfEmployer || "",
      businessNature: work.businessNature || "",
      annualIncomeAmount: typeof work.annualIncomeAmount === "number" ? work.annualIncomeAmount : "",
      sourceOfFunds: work.sourceOfFunds || "",
    },
    risk: { appetite: u.riskProfile || "" },
    settlement: {
      accountName: ad.accountName || "",
      bankName: ad.bankName || "",
      bankAccountNumber: ad.bankAccountNumber || "",
      savingsAccount: typeof ad.savingsAccount === "boolean" ? ad.savingsAccount : "",
    },
    docs: Array.isArray(k.idImages) ? k.idImages : [],
  };
}

function formatTs(ts: any): string {
  return ts ? moment(typeof ts.toDate === "function" ? ts.toDate() : ts).format("LL") : "";
}

function applicationStatusLabel(validated: string | undefined): string {
  if (validated === "verified") return "Verified";
  if (validated === "pending") return "Under review";
  if (validated === "declined") return "Declined";
  return "Submitted";
}

const AccountProfileDesktop: React.FC<Props> = ({ firebase, userDoc, kyc, accountDetails }) => {
  const classes = useStyles();
  const history = useHistory();
  const [section, setSection] = useState<Section>("overview");

  const tier = normalizeTier(userDoc?.stats?.tier);
  const validated: string | undefined = kyc?.validated;
  const statusCode: string | undefined = userDoc?.stats?.status;
  const accountStatus = getAccountStatus(validated, tier, statusCode);
  const completeness = computeCompleteness(tier, validated);
  const firstName = userDoc?.firstName || "";
  const lastName = userDoc?.lastName || "";
  const fullName = `${firstName} ${lastName}`.trim();
  const accountId = userDoc?.accountNumber || "\u2014";

  // A profile-update review awaiting admin approval.
  const updatePending =
    userDoc?.accountUpdatePending === true || Boolean(userDoc?.review?.pendingSubmissionId);

  // Only verified Tier 2/3 clients can use the account-update review flow.
  const canUpdate = validated === "verified" && (tier === 2 || tier === 3);
  const goUpdate = () => {
    if (canUpdate) history.push(ROUTES.UPDATE_KYC);
  };

  const overviewData = useMemo(
    () => buildSectionData({ user: userDoc, kyc, accountDetails, email: userDoc?.email || "" }),
    [userDoc, kyc, accountDetails]
  );

  if (!userDoc) {
    return (
      <div className={classes.page}>
        <div style={{ display: "grid", placeItems: "center", minHeight: 400 }}>
          <CircularProgress style={{ color: T.green }} />
        </div>
      </div>
    );
  }

  let content: React.ReactNode;
  if (section === "password") {
    content = <ChangePasswordCard firebase={firebase} />;
  } else if (section === "application") {
    content = (
      <MyApplicationCard
        kyc={kyc}
        accountDetails={accountDetails}
        riskProfile={userDoc?.riskProfile || ""}
        mobileNumber={userDoc?.mobileNumber || ""}
        statusLabel={applicationStatusLabel(validated)}
        submittedText={userDoc?.dateKYCCompleted ? `Submitted ${formatTs(userDoc.dateKYCCompleted)}` : ""}
      />
    );
  } else {
    content = <AccountSections data={overviewData} verified={validated === "verified"} />;
  }

  return (
    <div className={classes.page}>
      <div className={classes.layout}>
        <ProfilePanel
          name={fullName || "\u2014"}
          initials={getInitials(firstName, lastName)}
          accountId={accountId}
          tierLabel={tierLabel(tier)}
          pct={completeness}
          statusLabel={accountStatus.pill}
          updatePending={updatePending}
          canUpdateInfo={canUpdate}
          onUpdateInfo={goUpdate}
          onChangePassword={() => setSection("password")}
        />
        <div className={classes.right}>
          <StatusBanner status={accountStatus} />
          <SectionNav active={section} onSelect={setSection} />
          {content}
        </div>
      </div>
    </div>
  );
};

export default AccountProfileDesktop;
